import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, X, Check } from 'lucide-react';
import { Button } from '@heyroot/ui/primitives/Button';
import { Textarea } from '@heyroot/ui/primitives/Textarea';
import { ConfirmModal } from '@heyroot/backoffice-ui';
import { createNote, updateNote, deleteNote, queryKeys } from '../lib/api';
import type { TenantNote } from '../lib/api';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';

interface NotesPanelProps {
  tenantId: string;
  notes: TenantNote[];
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function NotesPanel({ tenantId, notes }: NotesPanelProps) {
  const { hasPerm } = useAdminAuth();
  const queryClient = useQueryClient();
  const canWrite = hasPerm(PERMS.TENANT_NOTES_WRITE);

  const [addBody, setAddBody] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.tenant(tenantId) });
  }

  const createMutation = useMutation({
    mutationFn: (body: string) => createNote(tenantId, body),
    onSuccess: () => {
      setAddBody('');
      setAddError(null);
      invalidate();
    },
    onError: (e) => setAddError(e instanceof Error ? e.message : 'Failed to create note.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ noteId, body }: { noteId: string; body: string }) => updateNote(noteId, body),
    onSuccess: () => {
      setEditingId(null);
      setEditBody('');
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => deleteNote(noteId),
    onSuccess: () => {
      setDeleteTargetId(null);
      invalidate();
    },
  });

  function startEdit(note: TenantNote) {
    setEditingId(note.id);
    setEditBody(note.body);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditBody('');
  }

  return (
    <section className="rounded-control border border-border bg-surface-elevated p-5">
      <h2 className="text-eyebrow uppercase text-muted mb-4">Notes</h2>

      {/* Note list */}
      {notes.length === 0 && !canWrite && <p className="text-body-sm text-muted">No notes.</p>}

      {notes.length > 0 && (
        <ul role="list" className="list-none p-0 mb-4 flex flex-col gap-2.5">
          {notes.map((note) => (
            <li
              key={note.id}
              className="rounded-control border border-border bg-surface px-3.5 py-3"
            >
              {editingId === note.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    rows={3}
                    aria-label="Edit note body"
                  />
                  <div className="flex gap-1.5 justify-end">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className={iconBtnCls}
                      aria-label="Cancel edit"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (editBody.trim())
                          updateMutation.mutate({ noteId: note.id, body: editBody.trim() });
                      }}
                      disabled={updateMutation.isPending || !editBody.trim()}
                      className={`${iconBtnCls} text-success-text`}
                      aria-label="Save note"
                    >
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <p className="m-0 text-body-sm text-primary whitespace-pre-wrap">{note.body}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-caption text-muted">
                      {formatDate(
                        note.updatedAt !== note.createdAt ? note.updatedAt : note.createdAt,
                      )}
                      {note.updatedAt !== note.createdAt && ' (edited)'}
                    </span>
                    {canWrite && (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(note)}
                          className={iconBtnCls}
                          aria-label="Edit note"
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTargetId(note.id)}
                          disabled={deleteMutation.isPending}
                          className={`${iconBtnCls} text-danger-text`}
                          aria-label="Delete note"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add note */}
      {canWrite && (
        <div
          className={`flex flex-col gap-2${notes.length > 0 ? ' border-t border-border pt-3' : ''}`}
        >
          <Textarea
            value={addBody}
            onChange={(e) => setAddBody(e.target.value)}
            rows={2}
            placeholder="Add a note…"
            aria-label="New note body"
          />
          {addError && <span className="text-caption text-danger-text">{addError}</span>}
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                if (addBody.trim()) createMutation.mutate(addBody.trim());
              }}
              disabled={createMutation.isPending || !addBody.trim()}
              leftIcon={<Plus className="h-3.5 w-3.5" aria-hidden="true" />}
            >
              {createMutation.isPending ? 'Adding…' : 'Add Note'}
            </Button>
          </div>
        </div>
      )}

      {/* code-review fix (full-app-audit-2026-08-29 follow-up): a raw
          window.confirm() was a single-click-plus-native-alert away from an
          irreversible delete, and was inconsistent with this same app's
          existing ConfirmModal usage (OnboardingTab.tsx). */}
      <ConfirmModal
        open={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={() => {
          if (deleteTargetId) deleteMutation.mutate(deleteTargetId);
        }}
        title="Delete this note?"
        confirmLabel="Delete"
        destructive
        isLoading={deleteMutation.isPending}
      >
        <p className="text-body-sm text-secondary">This cannot be undone.</p>
      </ConfirmModal>
    </section>
  );
}

const iconBtnCls =
  'inline-flex items-center justify-center h-6 w-6 p-0 rounded-control border-none bg-transparent text-secondary cursor-pointer hover:bg-surface-sunken hover:text-primary transition-colors duration-[var(--duration-fast)]';
